/**
 * PATH: packages/ui/src/kanban/components/lane/useKanbanLane.ts
 * FILE_ID: UI-KANBAN-LANE-VM-001
 * PURPOSE: Lane view-model: drop line, card order, and the menu search filter.
 * ROLE IN ARCHITECTURE: Hook behind KanbanLane. Menu, rename, and AI bulk
 *   live in sibling hooks so this file stays a composer.
 * MAIN EXPORTS: useKanbanLane, LaneVM
 * NON-RESPONSIBILITIES: Does not render the lane.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { splitCardsByAllocationSource } from '../../allocation-source'
import { useTriageTabUrl } from '../../use-triage-tab-url'
import { compareCards, flattenCardTree } from '../../utils'
import { computeDropBoundaryY } from '../../utils/cardReorder'
import { MULTI_CARD_MIME, dragCardIds } from '../../utils/cardSelection'
import { computeQuoteLaneRollup, isQuoteLaneKind } from '../../utils/quoteLaneValue'
import { KanbanCard } from '../KanbanCard'
import { KanbanChecklistRow } from '../KanbanChecklistRow'
import { CARD_RENDER_CAP, EMPTY_SELECTION, KANBAN_CARD_MIME, KANBAN_PLACEMENT_MIME } from './constants'
import { registerTouchDropLane, unregisterTouchDropLane } from './touchDragRegistry'
import { cardMatchesSearch } from '../../filters/model'
import { useInboxLaneFilters } from './inbox-filters'
import { useLaneAiBulk } from './useLaneAiBulk'
import { useLaneMenu } from './useLaneMenu'
import { useLaneRename } from './useLaneRename'
import type { Card as KanbanCardModel } from '../../types'
import type { KanbanLaneProps } from './types'

export function useKanbanLane(props: KanbanLaneProps) {
  const {
    lane,
    cards,
    onRename,
    api,
    onAiAllocationChanged,
    notify,
    selectedCardIds,
    onCardSelectToggle,
    onCardSelectRange,
    sortComparator,
    onCardDrop,
    showInboxLaneFilters,
    boardId,
    listInboxEmails,
    buildInboxListUrl,
    unseenCounts,
  } = props

  const inboxLaneFilters = useInboxLaneFilters({
    enabled: !!showInboxLaneFilters,
    boardId,
    cards,
    unseenByCard: unseenCounts,
    listInboxEmails,
    buildInboxListUrl,
  })
  const cardsForLane = inboxLaneFilters.enabled
    ? inboxLaneFilters.filteredCards
    : cards
  const [colorPickerOpen, setColorPickerOpen] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  // FEAT-2026-07-03-KANBAN-DROP-LINE — content-space Y (px, relative to the
  // lane's scroll container) at which to draw the live insertion line while a
  // single card is dragged over this lane. `null` = no line (not dragging, an
  // empty lane, or a multi-card drag which keeps the legacy append). See
  // updateDropLine / clearDropLine below.
  const [dropLineTop, setDropLineTop] = useState<number | null>(null)
  const {
    menuOpen,
    setMenuOpen,
    laneSearch,
    setLaneSearch,
    menuRef,
  } = useLaneMenu()
  const {
    renaming,
    setRenaming,
    draftName,
    setDraftName,
    renameRef,
    commitRename,
  } = useLaneRename(lane, onRename)

  // FEAT-2026-06-15-KANBAN-MULTISELECT — every card drag stamps the
  // single-card MIME (back-compat with the lane-reorder + nest drop
  // targets) AND, when the grabbed card is part of a >1 multi-selection,
  // the full id list under MULTI_CARD_MIME so a drop-as-child nests the
  // whole set. Shared by the tile + checklist-row render branches.
  const handleCardDragStart = useCallback(
    (card: KanbanCardModel, e: React.DragEvent) => {
      const ids = dragCardIds(selectedCardIds ?? EMPTY_SELECTION, card.id)
      e.dataTransfer.setData(KANBAN_CARD_MIME, card.id)
      // ARC-10 linked cards — a projected placement instance drags ITS
      // presence, not the home card. The placement id rides its own MIME
      // so the drop handler routes to the placement PATCH.
      if (card.placement_id) {
        e.dataTransfer.setData(KANBAN_PLACEMENT_MIME, card.placement_id)
      }
      if (ids.length > 1) {
        e.dataTransfer.setData(MULTI_CARD_MIME, JSON.stringify(ids))
      }
      e.dataTransfer.effectAllowed = 'move'
    },
    [selectedCardIds],
  )

  // FEAT-2026-07-03-KANBAN-DROP-LINE — live insertion-line position.
  //
  // The lane's onDragOver fires continuously while a card is in flight; reading
  // the DOM (getBoundingClientRect on every mounted tile) on each event would
  // thrash layout. So we stash the latest pointer position in a ref and coalesce
  // the measurement into ONE requestAnimationFrame per frame. The measured
  // boundary Y (viewport) is converted into the scroll container's own content
  // coordinate space (`- containerTop + scrollTop`) so the absolutely-positioned
  // line in LaneBody lands exactly on the gap and scrolls with the cards.
  //
  // Uses `computeDropBoundaryY` — the SAME index math the drop handler uses — so
  // the previewed line can never disagree with where the card actually lands.
  const dropRafRef = useRef<number | null>(null)
  const pendingDropRef = useRef<{ laneEl: HTMLElement; clientY: number } | null>(
    null,
  )
  const clearDropLine = useCallback(() => {
    if (dropRafRef.current != null) {
      cancelAnimationFrame(dropRafRef.current)
      dropRafRef.current = null
    }
    pendingDropRef.current = null
    setDropLineTop((prev) => (prev === null ? prev : null))
  }, [])
  const updateDropLine = useCallback((laneEl: HTMLElement, clientY: number) => {
    pendingDropRef.current = { laneEl, clientY }
    if (dropRafRef.current != null) return
    dropRafRef.current = requestAnimationFrame(() => {
      dropRafRef.current = null
      const pending = pendingDropRef.current
      pendingDropRef.current = null
      if (!pending) return
      const scroller = pending.laneEl.querySelector<HTMLElement>(
        '[data-lane-scroll]',
      )
      if (!scroller) {
        setDropLineTop((prev) => (prev === null ? prev : null))
        return
      }
      const tiles = Array.from(
        pending.laneEl.querySelectorAll<HTMLElement>('[data-card-id]'),
      )
      const rects = tiles.map((t) => t.getBoundingClientRect())
      const boundaryY = computeDropBoundaryY(pending.clientY, rects)
      if (boundaryY === null) {
        setDropLineTop((prev) => (prev === null ? prev : null))
        return
      }
      const scRect = scroller.getBoundingClientRect()
      const top = boundaryY - scRect.top + scroller.scrollTop
      // Sub-pixel jitter would re-render every frame for no visible gain.
      setDropLineTop((prev) =>
        prev != null && Math.abs(prev - top) < 0.5 ? prev : top,
      )
    })
  }, [])
  // Belt-and-braces: cancel any pending frame if the lane unmounts mid-drag.
  useEffect(
    () => () => {
      if (dropRafRef.current != null) cancelAnimationFrame(dropRafRef.current)
    },
    [],
  )

  // MOBILE touch drag (UI-KANBAN-LANE-TOUCHDRAG-001) — register this lane as
  // a touch drop target. The touch path hit-tests `[data-lane-id]` in the DOM
  // and routes the drop through this entry into the SAME `onCardDrop` the
  // HTML5 drop uses (identical move mutation / undo / optimistic path). Refs
  // keep the entry stable while always reading the current props.
  const touchDropRef = useRef({ lane, cards, onCardDrop })
  touchDropRef.current = { lane, cards, onCardDrop }
  useEffect(() => {
    registerTouchDropLane(lane.id, {
      getCards: () => touchDropRef.current.cards,
      drop: (cardId, position, placementId) => {
        const cur = touchDropRef.current
        cur.onCardDrop?.(cur.lane, cardId, undefined, position, placementId, undefined)
      },
    })
    return () => unregisterTouchDropLane(lane.id)
  }, [lane.id])

  // FEAT-2026-06-22-KANBAN-NOTEBOOKS — Shift-range select. The lane's
  // visual order lives in `flattened` (computed below), mirrored into a
  // ref each render so this handler can stay STABLE (passing a fresh
  // function per render would bust KanbanCard's memoisation and re-render
  // every tile). A plain modifier-click forwards to the board's toggle;
  // a Shift-click resolves the contiguous range over the lane order.
  const orderedCardIdsRef = useRef<string[]>([])
  const handleCardSelect = useCallback(
    (card: KanbanCardModel, additive: boolean, range?: boolean) => {
      if (range && onCardSelectRange) {
        onCardSelectRange(orderedCardIdsRef.current, card.id)
        return
      }
      onCardSelectToggle?.(card, additive)
    },
    [onCardSelectRange, onCardSelectToggle],
  )
  // Module D — UC|AI segmented tab on TRIAGE / INBOX_PRIVATE lanes.
  // 'all' is the default so adopters who haven't enabled the AI
  // suggestion lifecycle see no change. The tab choice is synced
  // to `?triage_<laneId>=` in the URL so operators can deep-link or
  // share a "look at the AI bucket" view.
  const [triageTab, setTriageTab] = useTriageTabUrl({
    initial: 'all',
    urlScope: lane.id,
  })

  // 2026-05-22 STUCK-OVERLAY FIX — when a child (KanbanCard /
  // KanbanChecklistRow) handles a drop with `stopPropagation()`, the
  // lane's own `onDrop` never fires, so `setDragOver(false)` never
  // runs. The dashed cyan overlay (rendered when `dragOver` is true)
  // gets stuck on after every successful nest drop. A global
  // `dragend` listener on `document` resets it regardless of which
  // descendant accepted the drop — `dragend` always fires on the
  // SOURCE of the drag exactly once. We also reset on `drop` at the
  // window level as a belt-and-braces fallback in case the source is
  // removed from the DOM before `dragend` lands (e.g. the row was
  // re-rendered after an optimistic update).
  useEffect(() => {
    if (!dragOver) return
    // Also drops the FEAT-2026-07-03 insertion line: `dragend`/`drop` fire on
    // the drag SOURCE exactly once, so this guarantees the line clears even
    // when the drop was accepted by a descendant (card nest) whose
    // stopPropagation kept the lane's own onDrop from running.
    const clear = () => {
      setDragOver(false)
      clearDropLine()
    }
    window.addEventListener('dragend', clear)
    window.addEventListener('drop', clear)
    return () => {
      window.removeEventListener('dragend', clear)
      window.removeEventListener('drop', clear)
    }
  }, [dragOver, clearDropLine])

  // Module D — split cards by allocation source so the Triage lane
  // can offer a UC | AI segmented filter. Pure I→O; we always
  // compute the split (it's O(n) over the lane's cards) but only
  // surface the segmented control on lane kinds where it's useful:
  //   - TRIAGE  (the canonical "to be allocated" lane on INBOX boards)
  //   - INBOX_PRIVATE (per-user inbox surface; same semantic)
  // PERF-2026-06-22-KANBAN-LANE-MEMO — these were recomputed O(n) over the
  // lane's cards on EVERY render (incl. parent re-renders that didn't touch
  // this lane). Memoising on [cards]/[visibleCards] keeps values identical
  // while also stabilising the references that feed the existing
  // `hiddenDoneSubtreeIds` / `flattened` memos (previously defeated on the
  // AI-tab path because `allocationSplit`/`flattenedAll` were fresh refs).
  const searchedCards = useMemo(() => {
    const query = laneSearch.trim()
    if (!query) return cardsForLane
    return cardsForLane.filter((card) => cardMatchesSearch(card, query))
  }, [cardsForLane, laneSearch])
  const allocationSplit = useMemo(
    () => splitCardsByAllocationSource(searchedCards),
    [searchedCards],
  )
  const showUcAiTabs =
    (lane.kind === 'TRIAGE' || lane.kind === 'INBOX_PRIVATE') &&
    allocationSplit.ai.length > 0
  const visibleCards = useMemo(
    () =>
      showUcAiTabs
        ? triageTab === 'ai'
          ? allocationSplit.ai
          : triageTab === 'uc'
            ? allocationSplit.uc
            : searchedCards
        : searchedCards,
    [showUcAiTabs, triageTab, allocationSplit, searchedCards],
  )
  // Stack 12 MR-C — render cards in depth-first parent→children order
  // so sub-cards appear immediately below their parent, indented by
  // their tree depth. `flattenCardTree` preserves the active comparator's
  // ordering WITHIN sibling groups. The legacy flat-`sorted` projection is
  // kept for downstream consumers (segmented control counts, etc.) that
  // want the un-flattened set.
  //
  // `sortComparator` (Time view passes date-descending) overrides the
  // default `compareCards` so the caller's chosen order actually survives
  // this re-sort instead of being silently reverted to priority → due.
  const cmp = sortComparator ?? compareCards
  const sorted = useMemo(() => [...visibleCards].sort(cmp), [visibleCards, cmp])
  const flattenedAll = useMemo(
    () => flattenCardTree(visibleCards, cmp),
    [visibleCards, cmp],
  )

  // BUG-2026-05-22-KANBAN-CHECKLIST-DONE-STILL-VISIBLE — operator
  // complaint: *"why is this striked off action still visible? If I
  // pressed c / Archive it should be still visible"* (typo for "not
  // visible"). Pressing `c` in checklist mode sets status=DONE
  // (operator's qb!2870 spec — tick keeps the card so they can
  // un-tick without restoring an archive), but the strikethrough
  // row staying inline up at the top of the lane creates the same
  // visual debt operators complain about in Trello / Notion. We
  // mirror the Things 3 / Apple Reminders / Linear pattern: DONE
  // cards collapse into a sticky "Done (N) — show / hide" footer
  // below the active rows. The toggle is per-lane local state so
  // the operator can drop in to clear / un-tick / archive without
  // a roundtrip to a filters bar they have to discover first.
  //
  // Tile mode is unchanged — operator expectation there is the
  // standard kanban "card stays where you put it" UX.
  const isChecklistMode = lane.display_mode === 'checklist'
  const [showDone, setShowDone] = useState(false)
  // PERF-2026-06-09-KANBAN-HUGE-LANE-RENDER-CAP — a lane returns its
  // full open-card set with no server truncation (qb!2781), so a
  // triage/inbox lane with thousands of cards (e.g. "Triage (9533)")
  // tried to mount thousands of <KanbanCard>s in one synchronous pass
  // and locked the browser ("Page Unresponsive") — hit hard when the
  // calendar's "Open Kanban card" deep-links straight into such a
  // board. We cap the INITIAL render at CARD_RENDER_CAP and surface a
  // "Show all N" expander; nothing is hidden permanently and lanes
  // under the cap are completely unaffected. State is per-lane (the
  // component is keyed by lane id) so it resets cleanly on board
  // switch.
  //
  // BUG-2026-06-29-KANBAN-CARDS-HIDDEN-BEHIND-CAP — operator: "not all
  // emails are being shown". The cap was correct (a 9533-card lane mounted
  // at once locked the browser) but the only way past it was a faint
  // dashed button buried BELOW 150 cards, so a 325-card Overdue lane just
  // looked truncated. We now ALSO grow the cap incrementally as the
  // operator scrolls to the bottom (infinite-scroll via an
  // IntersectionObserver on the expander), so every card is reachable by
  // scrolling while the INITIAL mount stays bounded. The explicit button
  // still reveals everything in one go for those who want it.
  const [showAllCards, setShowAllCards] = useState(false)
  const [extraRender, setExtraRender] = useState(0)
  const showAllRef = useRef<HTMLButtonElement | null>(null)
  // Hide a DONE card AND all of its descendants when the toggle is
  // off; otherwise a half-collapsed subtree would dangle sub-cards
  // out from under a hidden parent and the indent would be wrong.
  // We do the subtree walk on `visibleCards` (post-filter) so the
  // closure matches whatever the lane is actually rendering.
  const hiddenDoneSubtreeIds = useMemo(() => {
    if (!isChecklistMode || showDone) return new Set<string>()
    const hidden = new Set<string>()
    const byParent = new Map<string, string[]>()
    for (const c of visibleCards) {
      const parentId = c.parent_card
      if (!parentId) continue
      const arr = byParent.get(parentId) ?? []
      arr.push(c.id)
      byParent.set(parentId, arr)
    }
    for (const c of visibleCards) {
      if (c.status !== 'DONE') continue
      const stack: string[] = [c.id]
      while (stack.length) {
        const id = stack.pop()!
        if (hidden.has(id)) continue
        hidden.add(id)
        const children = byParent.get(id)
        if (children) stack.push(...children)
      }
    }
    return hidden
  }, [isChecklistMode, showDone, visibleCards])
  const flattened = useMemo(
    () =>
      hiddenDoneSubtreeIds.size === 0
        ? flattenedAll
        : flattenedAll.filter(({ card: c }) => !hiddenDoneSubtreeIds.has(c.id)),
    [flattenedAll, hiddenDoneSubtreeIds],
  )
  // FEAT-2026-06-22-KANBAN-NOTEBOOKS — keep the Shift-range handler's
  // ordered-id source current. Uses the FULL flattened order (not the
  // render-capped slice) so a range can span cards hidden behind the
  // "Show all" cap. Cheap: one map over an already-memoised list.
  orderedCardIdsRef.current = flattened.map(({ card: c }) => c.id)
  // Initial-render cap (see showAllCards above). Slice the already-
  // computed flattened tree so capping costs nothing for normal lanes.
  // `extraRender` grows by a page each time the operator scrolls the
  // expander into view, so the lane reveals more on scroll without a click.
  const effectiveCap = CARD_RENDER_CAP + extraRender
  const hiddenCardCount = showAllCards
    ? 0
    : Math.max(0, flattened.length - effectiveCap)
  const renderList = hiddenCardCount > 0 ? flattened.slice(0, effectiveCap) : flattened
  // Infinite-scroll: when the "Show all" expander scrolls into view, grow
  // the cap by one page. Each bump pushes the expander back below the fold
  // so the observer un-intersects and only re-fires on the next scroll —
  // bounded, one page per scroll, never a single thousands-card mount.
  // Guarded for jsdom / SSR where IntersectionObserver is absent.
  useEffect(() => {
    if (showAllCards || hiddenCardCount === 0) return
    const el = showAllRef.current
    if (!el || typeof IntersectionObserver === 'undefined') return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setExtraRender((n) => n + CARD_RENDER_CAP)
        }
      },
      { rootMargin: '300px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [showAllCards, hiddenCardCount])
  const doneCount = useMemo(
    () =>
      isChecklistMode
        ? visibleCards.reduce((n, c) => (c.status === 'DONE' ? n + 1 : n), 0)
        : 0,
    [isChecklistMode, visibleCards],
  )
  const overWipLimit = lane.wip_limit > 0 && cards.length > lane.wip_limit

  // CRM-QUOTE-PIPELINE — value rollup for the Quote Pipeline lane header.
  // Each funnel stage shows Σ value + the risk-weighted figure (Σ value ×
  // win%) so an operator sees "how much is sitting in Submitted, and what's
  // it worth weighted by likelihood". Pure I→O (utils/quoteLaneValue);
  // null on non-quote lanes / lanes with no valued cards so the chip is
  // simply omitted. Computed over the lane's full card set (matches the
  // count chip's `cards.length`).
  const isQuoteLane = isQuoteLaneKind(lane.kind)
  const quoteValue = useMemo(
    () => (isQuoteLane ? computeQuoteLaneRollup(cards) : null),
    [isQuoteLane, cards],
  )

  const { bulkAcceptAi, bulkRejectAi, showBulkBar } = useLaneAiBulk({
    api,
    notify,
    onAiAllocationChanged,
    aiCards: allocationSplit.ai,
    showUcAiTabs,
    triageTab,
  })

  return {
    colorPickerOpen,
    setColorPickerOpen,
    dragOver,
    setDragOver,
    dropLineTop,
    updateDropLine,
    clearDropLine,
    renaming,
    setRenaming,
    draftName,
    setDraftName,
    menuOpen,
    setMenuOpen,
    handleCardDragStart,
    handleCardSelect,
    triageTab,
    setTriageTab,
    renameRef,
    menuRef,
    allocationSplit,
    showUcAiTabs,
    sorted,
    isChecklistMode,
    showDone,
    setShowDone,
    setShowAllCards,
    showAllRef,
    flattened,
    hiddenCardCount,
    renderList,
    doneCount,
    overWipLimit,
    quoteValue,
    bulkAcceptAi,
    bulkRejectAi,
    showBulkBar,
    commitRename,
    laneSearch,
    setLaneSearch,
    searchedCount: searchedCards.length,
    inboxLaneFilters,
    /** Card universe after inbox filters (before UC/AI tabs). */
    cardsForLane,
  }
}

export type LaneVM = ReturnType<typeof useKanbanLane>
